import { Link } from 'react-router-dom';
import {
    Target,
    Layout,
    Link2,
    Check,
    X,
    ArrowRight,
    Zap,
    Shield,
    Users,
    Star,
    Clock,
    TrendingUp,
    AlertTriangle,
    Sparkles,
    Play,
    CheckCircle2,
    XCircle,
    Quote,
    Award,
    Rocket,
    Calendar,
    Brain,
    Lock,
} from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';

export default function LandingPage() {

    return (
        <div className="min-h-screen bg-white dark:bg-neutral-900 overflow-x-hidden">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-xl border-b border-neutral-200/50 dark:border-neutral-800/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 via-primary-600 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
                                <Target className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                                VisionBoard
                            </span>
                        </div>

                        <div className="hidden md:flex items-center gap-8">
                            <a href="#features" className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors">Tính năng</a>
                            <a href="#how-it-works" className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors">Cách hoạt động</a>
                            <a href="#testimonials" className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors">Đánh giá</a>
                            <Link to="/blog" className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors">Blog</Link>
                            <Link to="/docs" className="text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors">Hướng dẫn</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <ThemeToggle />
                            <Link to="/login" className="hidden sm:block text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors">
                                Đăng nhập
                            </Link>
                            <Link to="/register" className="btn-primary text-sm px-5 py-2.5 shadow-lg shadow-primary-500/30">
                                Bắt đầu miễn phí
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section - Problem Focused */}
            <section className="pt-28 sm:pt-36 pb-20 px-4 sm:px-6 lg:px-8 relative">
                {/* Background Effects */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-br from-primary-500/20 via-secondary-500/10 to-transparent rounded-full blur-3xl -z-10"></div>

                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-4xl mx-auto">
                        {/* 🎉 PROMOTIONAL BANNER */}
                        <div className="inline-flex flex-col items-center gap-3 mb-8">
                            {/* Primary Promo */}
                            <div className="px-6 py-3 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-2xl shadow-amber-500/50 animate-pulse-slow border-2 border-white/30">
                                <div className="flex items-center gap-3">
                                    <span className="text-3xl">🎉</span>
                                    <div>
                                        <p className="font-extrabold text-xl tracking-tight">
                                            NHẬN MIỄN PHÍ GÓI PRO 1 NĂM!
                                        </p>
                                        <p className="text-sm text-white/90 font-medium">
                                            ⏰ Đăng ký trước 1/1/2027 • Trị giá 1.200.000đ
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Trust Badge */}
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-600/20 dark:to-secondary-600/20 text-primary-700 dark:text-primary-400 text-sm font-medium border border-primary-100 dark:border-primary-600/30">
                                <Sparkles className="w-4 h-4" />
                                <span>Được 1,000+ người dùng tin tưởng</span>
                                <div className="flex -space-x-1 ml-2">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Main Headline - Problem */}
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-neutral-900 dark:text-white mb-6 leading-tight tracking-tight">
                            <span className="block">Công việc hàng ngày</span>
                            <span className="block bg-gradient-to-r from-primary-600 via-secondary-600 to-primary-600 bg-clip-text text-transparent">
                                không kết nối với mục tiêu?
                            </span>
                        </h1>

                        {/* Pain Points */}
                        <div className="flex flex-wrap justify-center gap-3 mb-8">
                            {[
                                { icon: Clock, text: 'Bận rộn cả ngày nhưng không tiến bộ' },
                                { icon: AlertTriangle, text: 'Không biết công việc nào quan trọng' },
                                { icon: XCircle, text: 'Mục tiêu đặt ra rồi lại quên' },
                            ].map((pain, i) => (
                                <div key={i} className="flex items-center gap-2 px-4 py-2 bg-danger-50 dark:bg-danger-600/20 text-danger-700 dark:text-danger-400 rounded-lg text-sm">
                                    <pain.icon className="w-4 h-4" />
                                    {pain.text}
                                </div>
                            ))}
                        </div>

                        {/* Solution - Slogan */}
                        <p className="text-xl text-neutral-600 dark:text-neutral-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                            <strong className="text-neutral-900 dark:text-white">VisionBoard</strong> —
                            <span className="text-primary-600 font-semibold"> Thấy rõ. Làm đúng. Đến đích.</span>
                            <br />
                            <span className="text-lg">Kết hợp OKR và Kanban để mỗi việc bạn làm đều có ý nghĩa.</span>
                        </p>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                            <Link to="/register" className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-lg rounded-2xl hover:from-amber-600 hover:to-orange-600 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                                <Rocket className="w-6 h-6 group-hover:-translate-y-1 transition-transform" />
                                Nhận PRO 1 năm MIỄN PHÍ ngay!
                            </Link>
                            <button className="group flex items-center gap-2 px-6 py-4 text-neutral-600 dark:text-neutral-400 hover:text-primary-600 transition-colors">
                                <div className="w-10 h-10 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                                    <Play className="w-4 h-4 ml-0.5" />
                                </div>
                                Xem demo 2 phút
                            </button>
                        </div>

                        {/* No Credit Card + Promo Info */}
                        <div className="space-y-2">
                            <p className="text-sm text-neutral-500 flex items-center justify-center gap-2">
                                <Shield className="w-4 h-4 text-success-500" />
                                Không cần thẻ tín dụng • Setup trong 30 giây
                            </p>
                            <p className="text-xs text-amber-600 dark:text-amber-400 font-semibold flex items-center justify-center gap-1">
                                <Award className="w-4 h-4" />
                                Ưu đãi đặc biệt: PRO miễn phí 12 tháng cho người đăng ký mới!
                            </p>
                        </div>
                    </div>

                    {/* Hero Image - Premium Dashboard Preview */}
                    <div className="mt-16 relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-neutral-900 via-transparent to-transparent z-10 pointer-events-none"></div>

                        {/* Floating Stats */}
                        <div className="hidden lg:block absolute -left-4 top-20 bg-white dark:bg-neutral-800 rounded-2xl p-4 shadow-2xl border border-neutral-200 dark:border-neutral-700 animate-float z-20">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-success-100 dark:bg-success-600/20 rounded-xl flex items-center justify-center">
                                    <TrendingUp className="w-6 h-6 text-success-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-success-600">+127%</p>
                                    <p className="text-xs text-neutral-500">Năng suất tăng</p>
                                </div>
                            </div>
                        </div>

                        <div className="hidden lg:block absolute -right-4 top-40 bg-white dark:bg-neutral-800 rounded-2xl p-4 shadow-2xl border border-neutral-200 dark:border-neutral-700 animate-float-delayed z-20">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-600/20 rounded-xl flex items-center justify-center">
                                    <Target className="w-6 h-6 text-primary-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-primary-600">94%</p>
                                    <p className="text-xs text-neutral-500">Mục tiêu đạt được</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-primary-500/20 via-secondary-500/10 to-primary-500/20 rounded-3xl p-2 sm:p-4 shadow-2xl border border-neutral-200/50 dark:border-neutral-700/50 backdrop-blur">
                            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-inner overflow-hidden">
                                <img
                                    src="/images/dashboard-preview.png"
                                    alt="VisionBoard Dashboard"
                                    className="w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Problem Agitation Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-800/50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                            Bạn có đang gặp phải vấn đề này?
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                problem: 'Đặt mục tiêu đầu năm rồi quên',
                                icon: Calendar,
                                description: 'Viết goal hoành tráng nhưng sau 2 tuần không ai nhớ',
                                color: 'danger',
                            },
                            {
                                problem: 'Không biết nên làm gì trước',
                                icon: Brain,
                                description: '100 việc cần làm nhưng không biết việc nào đóng góp vào mục tiêu',
                                color: 'warning',
                            },
                            {
                                problem: 'Bận rộn nhưng không tiến bộ',
                                icon: Clock,
                                description: 'Làm việc 10 tiếng/ngày mà cuối quý vẫn không đạt KPI',
                                color: 'secondary',
                            },
                        ].map((item, i) => (
                            <div key={i} className="bg-white dark:bg-neutral-800 rounded-2xl p-8 shadow-lg border border-neutral-200 dark:border-neutral-700">
                                <div className={`w-16 h-16 bg-${item.color}-100 dark:bg-${item.color}-600/20 rounded-2xl flex items-center justify-center mb-6`}>
                                    <item.icon className={`w-8 h-8 text-${item.color}-600`} />
                                </div>
                                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
                                    {item.problem}
                                </h3>
                                <p className="text-neutral-600 dark:text-neutral-400">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 text-center">
                        <p className="text-xl text-neutral-600 dark:text-neutral-400 mb-6">
                            Đó là vì bạn chưa có <strong className="text-primary-600">hệ thống</strong> kết nối công việc với mục tiêu.
                        </p>
                        <div className="flex items-center justify-center gap-2 text-3xl font-bold text-primary-600">
                            <ArrowRight className="w-8 h-8" />
                            VisionBoard là giải pháp!
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section - Solution */}
            <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-600/20 text-primary-600 text-sm font-medium mb-4">
                            <Zap className="w-4 h-4" />
                            Giải pháp toàn diện
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                            3 bước để biến mục tiêu thành hiện thực
                        </h2>
                        <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
                            VisionBoard kết hợp OKR (Phương pháp của Google) với Kanban để bạn vừa có tầm nhìn dài hạn, vừa hành động cụ thể.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        {[
                            {
                                step: 1,
                                icon: Target,
                                title: 'Đặt Mục tiêu Rõ ràng',
                                subtitle: 'OKR Framework',
                                description: 'Xác định điều bạn muốn đạt (Objective) và cách đo lường thành công (Key Results). Không còn mục tiêu mơ hồ!',
                                example: 'VD: "Tăng thu nhập phụ" → "Kiếm 10 triệu/tháng từ Freelance"',
                                color: 'primary',
                            },
                            {
                                step: 2,
                                icon: Layout,
                                title: 'Chia nhỏ thành Task',
                                subtitle: 'Kanban Board',
                                description: 'Biến mục tiêu lớn thành những việc nhỏ có thể làm ngay. Kéo-thả để theo dõi tiến độ trực quan.',
                                example: 'VD: "Tạo portfolio", "Đăng 5 bài LinkedIn", "Apply 10 dự án"',
                                color: 'secondary',
                            },
                            {
                                step: 3,
                                icon: Link2,
                                title: 'Liên kết & Theo dõi',
                                subtitle: 'Alignment Tree',
                                description: 'Xem cây alignment trực quan. AI phát hiện task lạc lõng và cảnh báo khi team lệch hướng!',
                                example: 'VD: "85% tasks đã liên kết" → Alignment Score cao',
                                color: 'success',
                            },
                        ].map((feature) => (
                            <div key={feature.step} className="group relative bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-xl border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-600 transition-all hover:-translate-y-2">
                                {/* Step Number */}
                                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                    {feature.step}
                                </div>

                                <div className={`w-16 h-16 bg-${feature.color}-100 dark:bg-${feature.color}-600/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                    <feature.icon className={`w-8 h-8 text-${feature.color}-600`} />
                                </div>

                                <p className={`text-sm font-medium text-${feature.color}-600 mb-2`}>{feature.subtitle}</p>
                                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                                    {feature.description}
                                </p>
                                <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-3">
                                    <p className="text-sm text-neutral-500 dark:text-neutral-400 italic">
                                        {feature.example}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <Link to="/register" className="btn-primary px-8 py-4 text-lg gap-2 shadow-lg">
                            Bắt đầu miễn phí ngay
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* How It Works - Visual */}
            <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-neutral-800 dark:via-neutral-900 dark:to-neutral-800">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                            Xem VisionBoard hoạt động
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8">
                            {[
                                {
                                    num: '01',
                                    title: 'Đặt Objective Q1 2025',
                                    desc: '"Phát triển thu nhập thụ động từ side project"',
                                },
                                {
                                    num: '02',
                                    title: 'Thêm Key Results',
                                    desc: '• Ra mắt 1 SaaS MVP\n• Có 100 users đầu tiên\n• Kiếm $500 MRR',
                                },
                                {
                                    num: '03',
                                    title: 'Tạo Tasks hành động',
                                    desc: 'Research → Design → Code → Launch → Marketing',
                                },
                                {
                                    num: '04',
                                    title: 'Theo dõi tiến độ realtime',
                                    desc: 'Dashboard cập nhật tự động khi hoàn thành task',
                                },
                            ].map((step, i) => (
                                <div key={i} className="flex gap-6 items-start">
                                    <div className="flex-shrink-0 w-14 h-14 bg-primary-600 text-white rounded-2xl flex items-center justify-center font-bold text-lg">
                                        {step.num}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-1">
                                            {step.title}
                                        </h3>
                                        <p className="text-neutral-600 dark:text-neutral-400 whitespace-pre-line">
                                            {step.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="bg-white dark:bg-neutral-800 rounded-3xl p-6 shadow-2xl border border-neutral-200 dark:border-neutral-700">
                            <img
                                src="https://placehold.co/600x500/f8fafc/64748b?text=OKR+%2B+Kanban+Demo"
                                alt="VisionBoard Demo"
                                className="w-full h-auto rounded-xl"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                            Tại sao chọn VisionBoard?
                        </h2>
                        <p className="text-lg text-neutral-600 dark:text-neutral-400">
                            Giải pháp duy nhất kết hợp Goal Setting + Task Management
                        </p>
                    </div>

                    <div className="bg-white dark:bg-neutral-800 rounded-3xl shadow-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-600/10 dark:to-secondary-600/10">
                                    <th className="text-left px-6 py-5 font-semibold text-neutral-600 dark:text-neutral-300">
                                        Tính năng
                                    </th>
                                    <th className="text-center px-6 py-5">
                                        <span className="font-bold text-primary-600 text-lg">VisionBoard</span>
                                    </th>
                                    <th className="text-center px-6 py-5 font-medium text-neutral-400">Notion</th>
                                    <th className="text-center px-6 py-5 font-medium text-neutral-400">Trello</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { feature: 'OKR Goal Setting', vb: true, notion: false, trello: false },
                                    { feature: 'Kanban Board', vb: true, notion: true, trello: true },
                                    { feature: 'Task-Goal Linking', vb: true, notion: false, trello: false },
                                    { feature: 'Auto Progress Tracking', vb: true, notion: false, trello: false },
                                    { feature: 'Alignment Detection & Tree', vb: true, notion: false, trello: false },
                                    { feature: 'AI Chat Assistant', vb: true, notion: false, trello: false },
                                    { feature: 'Analytics Dashboard', vb: true, notion: true, trello: false },
                                    { feature: 'Team Collaboration', vb: true, notion: true, trello: true },
                                    { feature: 'Gói miễn phí đầy đủ', vb: true, notion: true, trello: true },
                                ].map((row, i) => (
                                    <tr key={i} className="border-t border-neutral-100 dark:border-neutral-700">
                                        <td className="px-6 py-4 font-medium text-neutral-700 dark:text-neutral-300">
                                            {row.feature}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <CheckCircle2 className="w-6 h-6 text-success-500 mx-auto" />
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {row.notion ? <Check className="w-5 h-5 text-neutral-400 mx-auto" /> : <X className="w-5 h-5 text-neutral-300 mx-auto" />}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            {row.trello ? <Check className="w-5 h-5 text-neutral-400 mx-auto" /> : <X className="w-5 h-5 text-neutral-300 mx-auto" />}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* AI Assistant Feature Section - NEW */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary-500/20 rounded-full blur-3xl"></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-sm font-medium mb-6 shadow-lg">
                            <Brain className="w-4 h-4" />
                            <span>Powered by AI</span>
                            <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">PRO</span>
                        </div>
                        <h2 className="text-4xl sm:text-5xl font-extrabold text-neutral-900 dark:text-white mb-6">
                            Trợ lý AI thông minh<br />
                            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                                Hỗ trợ 24/7, Bảo mật tuyệt đối
                            </span>
                        </h2>
                        <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto leading-relaxed">
                            VisionBoard AI không chỉ giúp bạn lập kế hoạch thông minh mà còn đảm bảo dữ liệu của bạn được bảo vệ an toàn tuyệt đối.
                        </p>
                    </div>

                    {/* Two-Column Layout */}
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        {/* Left: AI Capabilities */}
                        <div className="space-y-6">
                            <div className="bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-xl border border-neutral-200 dark:border-neutral-700">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <Brain className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
                                            Trợ lý lập kế hoạch thông minh
                                        </h3>
                                        <p className="text-neutral-600 dark:text-neutral-400">
                                            AI hiểu workspace của bạn và đưa ra gợi ý phù hợp
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {[
                                        {
                                            icon: TrendingUp,
                                            title: 'Phân tích tiến độ thông minh',
                                            desc: 'AI theo dõi OKR và tasks của bạn, phát hiện rủi ro sớm và đề xuất hành động khắc phục.',
                                        },
                                        {
                                            icon: Sparkles,
                                            title: 'Gợi ý ưu tiên công việc',
                                            desc: 'Dựa trên mục tiêu và deadline, AI giúp bạn quyết định nên làm gì tiếp theo.',
                                        },
                                        {
                                            icon: CheckCircle2,
                                            title: 'Tư vấn OKR chuyên nghiệp',
                                            desc: 'Hỏi AI cách viết Objective rõ ràng, đo lường Key Results hiệu quả.',
                                        },
                                        {
                                            icon: Zap,
                                            title: 'Trả lời ngay lập tức',
                                            desc: 'Chat realtime 24/7, không cần chờ đợi. AI luôn sẵn sàng hỗ trợ bạn.',
                                        },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-4 p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group">
                                            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-600/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                                <item.icon className="w-5 h-5 text-primary-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-neutral-900 dark:text-white mb-1">
                                                    {item.title}
                                                </h4>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                                    {item.desc}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right: Security & Privacy */}
                        <div className="space-y-6">
                            <div className="bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-xl border border-success-200 dark:border-success-800/30">
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="w-14 h-14 bg-gradient-to-br from-success-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                                        <Shield className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
                                            An toàn & Bảo mật tuyệt đối
                                        </h3>
                                        <p className="text-neutral-600 dark:text-neutral-400">
                                            Dữ liệu của bạn được bảo vệ ở mức cao nhất
                                        </p>
                                    </div>
                                </div>

                                {/* Security Guarantees */}
                                <div className="space-y-4 mb-6">
                                    {[
                                        {
                                            icon: Shield,
                                            title: 'Quyền riêng tư được đảm bảo',
                                            desc: 'AI CHỈ truy cập workspace bạn có quyền. TUYỆT ĐỐI không đọc dữ liệu của người khác.',
                                            color: 'success',
                                        },
                                        {
                                            icon: Lock,
                                            title: 'Đồng ý rõ ràng',
                                            desc: 'Bạn phải đồng ý chia sẻ dữ liệu trước khi chat. Có thể từ chối bất cứ lúc nào.',
                                            color: 'primary',
                                        },
                                        {
                                            icon: Users,
                                            title: 'Không chia sẻ cross-workspace',
                                            desc: 'AI KHÔNG BAO GIỜ trả lời câu hỏi về workspace/project của user khác.',
                                            color: 'secondary',
                                        },
                                        {
                                            icon: CheckCircle2,
                                            title: 'Xác thực workspace',
                                            desc: 'Mỗi request được verify membership trước khi AI xử lý.',
                                            color: 'warning',
                                        },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-start gap-4 p-4 bg-success-50 dark:bg-success-900/10 rounded-xl border border-success-100 dark:border-success-800/20">
                                            <div className={`w-10 h-10 bg-${item.color}-100 dark:bg-${item.color}-600/20 rounded-lg flex items-center justify-center flex-shrink-0`}>
                                                <item.icon className={`w-5 h-5 text-${item.color}-600`} />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-neutral-900 dark:text-white mb-1 flex items-center gap-2">
                                                    {item.title}
                                                    <CheckCircle2 className="w-4 h-4 text-success-500" />
                                                </h4>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                                    {item.desc}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Trust Badge */}
                                <div className="bg-gradient-to-r from-success-500/10 to-emerald-500/10 dark:from-success-600/20 dark:to-emerald-600/20 rounded-xl p-6 border border-success-200 dark:border-success-700/30">
                                    <div className="flex items-center gap-3 mb-3">
                                        <CheckCircle2 className="w-6 h-6 text-success-600" />
                                        <h4 className="font-bold text-success-900 dark:text-success-400 text-lg">
                                            Cam kết bảo mật
                                        </h4>
                                    </div>
                                    <p className="text-sm text-success-800 dark:text-success-300 leading-relaxed">
                                        VisionBoard tuân thủ GDPR và các chuẩn bảo mật quốc tế.
                                        AI của chúng tôi được thiết kế với <strong>Privacy-First</strong> -
                                        ưu tiên quyền riêng tư của bạn hơn tất cả.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-16 text-center">
                        <div className="inline-flex flex-col items-center gap-4 p-8 bg-white dark:bg-neutral-800 rounded-3xl shadow-2xl border border-neutral-200 dark:border-neutral-700">
                            <p className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Sẵn sàng trải nghiệm AI Assistant?
                            </p>
                            <Link to="/register" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold rounded-xl hover:from-primary-600 hover:to-secondary-600 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                                <Brain className="w-5 h-5" />
                                Nhận PRO 1 năm MIỄN PHÍ - Có AI Assistant
                            </Link>
                            <p className="text-sm text-neutral-500 flex items-center gap-2">
                                <Shield className="w-4 h-4 text-success-500" />
                                Đầy đủ tính năng AI • An toàn & Bảo mật
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-neutral-50 dark:bg-neutral-800/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-success-100 dark:bg-success-600/20 text-success-600 text-sm font-medium mb-4">
                            <Award className="w-4 h-4" />
                            Được yêu thích
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                            1,000+ người đã thay đổi cách làm việc
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                quote: "Cuối cùng tôi cũng biết mình đang làm việc để đạt gì. Mỗi task hoàn thành đều có ý nghĩa!",
                                name: "Nguyễn Thị Hương",
                                role: "Product Manager @ Startup",
                                rating: 5,
                            },
                            {
                                quote: "Từ khi dùng VisionBoard, team tôi đạt 95% OKR thay vì 60% như trước. Game changer thực sự.",
                                name: "Trần Minh Đức",
                                role: "Tech Lead @ Agency",
                                rating: 5,
                            },
                            {
                                quote: "Tính năng Alignment Tree giúp tôi nhìn thấy ngay task nào đang lạc lõng không gắn mục tiêu.",
                                name: "Lê Văn Nam",
                                role: "Freelancer",
                                rating: 5,
                            },
                        ].map((testimonial, i) => (
                            <div key={i} className="bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-lg border border-neutral-200 dark:border-neutral-700">
                                <Quote className="w-10 h-10 text-primary-200 dark:text-primary-800 mb-4" />
                                <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-6 leading-relaxed">
                                    "{testimonial.quote}"
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold">
                                        {testimonial.name.charAt(0)}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-neutral-900 dark:text-white">{testimonial.name}</p>
                                        <p className="text-sm text-neutral-500">{testimonial.role}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 mt-4">
                                    {[...Array(testimonial.rating)].map((_, j) => (
                                        <Star key={j} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Advanced Features Section - NEW */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 dark:bg-secondary-600/20 text-secondary-600 text-sm font-medium mb-4">
                            <Sparkles className="w-4 h-4" />
                            Tính năng nổi bật
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
                            Công cụ mạnh mẽ cho mọi nhu cầu
                        </h2>
                        <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
                            Từ AI Chat hỗ trợ đến Analytics chi tiết, VisionBoard có mọi thứ bạn cần.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            {
                                icon: Brain,
                                title: 'AI Chat Assistant',
                                desc: 'Hỏi đáp, phân tích OKR và nhận gợi ý thông minh từ AI.',
                                badge: 'PRO',
                                color: 'primary',
                            },
                            {
                                icon: TrendingUp,
                                title: 'Alignment Score',
                                desc: 'Đo lường mức độ kết nối giữa công việc và mục tiêu.',
                                badge: null,
                                color: 'success',
                            },
                            {
                                icon: Users,
                                title: 'Team Collaboration',
                                desc: 'Mời thành viên, theo dõi activity và làm việc nhóm hiệu quả.',
                                badge: null,
                                color: 'secondary',
                            },
                            {
                                icon: Shield,
                                title: 'Bảo mật 2FA',
                                desc: 'Xác thực 2 bước để bảo vệ tài khoản của bạn.',
                                badge: null,
                                color: 'warning',
                            },
                        ].map((feature, i) => (
                            <div key={i} className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700 hover:shadow-lg transition-shadow">
                                <div className="flex items-start justify-between mb-4">
                                    <div className={`w-12 h-12 bg-${feature.color}-100 dark:bg-${feature.color}-600/20 rounded-xl flex items-center justify-center`}>
                                        <feature.icon className={`w-6 h-6 text-${feature.color}-600`} />
                                    </div>
                                    {feature.badge && (
                                        <span className="px-2 py-1 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs font-bold rounded-full">
                                            {feature.badge}
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                    {feature.desc}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 grid md:grid-cols-3 gap-6">
                        {[
                            { title: 'Dark Mode', desc: 'Giao diện tối dễ chịu cho mắt', icon: '🌙' },
                            { title: 'Notifications', desc: 'Cảnh báo deadline và KR at risk', icon: '🔔' },
                            { title: 'Referral Program', desc: 'Mời bạn bè, nhận PRO miễn phí', icon: '🎁' },
                        ].map((item, i) => (
                            <div key={i} className="flex items-center gap-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4">
                                <span className="text-2xl">{item.icon}</span>
                                <div>
                                    <p className="font-medium text-neutral-900 dark:text-white">{item.title}</p>
                                    <p className="text-sm text-neutral-500">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
                {/* Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600"></div>
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">
                        Sẵn sàng đạt được mục tiêu của bạn?
                    </h2>

                    {/* Promo Highlight */}
                    <div className="inline-block px-6 py-3 mb-6 bg-amber-500/20 backdrop-blur-sm rounded-2xl border-2 border-amber-300/50">
                        <p className="text-2xl font-bold text-amber-100">
                            🎉 NHẬN MIỄN PHÍ GÓI PRO 1 NĂM KHI ĐĂNG KÝ NGAY!
                        </p>
                        <p className="text-sm text-amber-200 mt-1">
                            Ưu đãi đặc biệt đến hết 31/12/2026 • Trị giá 1.200.000đ
                        </p>
                    </div>

                    <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
                        Tham gia cùng 1,000+ người đang sử dụng VisionBoard để biến ước mơ thành hiện thực!
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                        <Link to="/register" className="group inline-flex items-center gap-3 px-12 py-6 bg-white text-primary-600 font-bold rounded-2xl hover:bg-neutral-100 transition-all shadow-2xl text-xl hover:scale-105">
                            <Rocket className="w-6 h-6 group-hover:-translate-y-1 transition-transform" />
                            Nhận PRO 1 năm MIỄN PHÍ
                        </Link>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-6 text-white/70 text-sm">
                        <span className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-success-400" />
                            Không cần thẻ tín dụng
                        </span>
                        <span className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-success-400" />
                            Setup trong 30 giây
                        </span>
                        <span className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-success-400" />
                            Miễn phí mãi mãi
                        </span>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-neutral-900 text-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
                        <div className="col-span-2">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                                    <Target className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-xl font-bold">VisionBoard</span>
                            </div>
                            <p className="text-neutral-400 text-sm mb-4">
                                Kết nối công việc hàng ngày với mục tiêu dài hạn. Phương pháp thông minh để đạt được nhiều hơn.
                            </p>
                            <div className="flex gap-4">
                                {['facebook', 'twitter', 'linkedin'].map((social) => (
                                    <a key={social} href="#" className="w-10 h-10 bg-neutral-800 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-colors">
                                        <span className="sr-only">{social}</span>
                                        <Users className="w-5 h-5" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">Sản phẩm</h4>
                            <ul className="space-y-3 text-neutral-400 text-sm">
                                <li><a href="#features" className="hover:text-white transition-colors">Tính năng</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Bảng giá</a></li>
                                <li><Link to="/docs" className="hover:text-white transition-colors">Hướng dẫn</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">Công ty</h4>
                            <ul className="space-y-3 text-neutral-400 text-sm">
                                <li><a href="#" className="hover:text-white transition-colors">Về chúng tôi</a></li>
                                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                                <li><Link to="/contact" className="hover:text-white transition-colors">Liên hệ</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4">Hỗ trợ</h4>
                            <ul className="space-y-3 text-neutral-400 text-sm">
                                <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
                                <li><Link to="/terms" className="hover:text-white transition-colors">Điều khoản</Link></li>
                                <li><Link to="/privacy" className="hover:text-white transition-colors">Bảo mật</Link></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-neutral-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className="text-neutral-400 text-sm">© 2025 VisionBoard. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
